import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, API_BASE } from '../../lib/api';
import { PARTNER_TERMS_VERSION } from '../PartnerTermsPage';
import PartnerClientApprovalMatrix, { compactPartnerMatrix, fullPartnerMatrix, type PartnerMatrix } from '../../components/clientApprovals/PartnerClientApprovalMatrix';
import { D9_COPY } from '../../lib/clientApprovals';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WizardState {
  id: string;
  partnerAccountId: string;
  currentStep: number;
  completedSteps: number[];
  step1Data: any;
  step2Data: any;
  step3Data: any;
  step3aData: any;
  step4Data: any;
  step5Data: any;
  completedAt: string | null;
}

interface StepProps {
  onSave: (data: any) => Promise<void>;
  onBack: () => void;
  isSaving: boolean;
  initialData: any;
}

// ── Step definitions ──────────────────────────────────────────────────────────

const STEPS = [
  { number: 1, title: 'Organisation Profile' },
  { number: 2, title: 'White-Label Branding' },
  { number: 3, title: 'Default Configuration' },
  { number: 4, title: 'Domain Pack Access' },
  { number: 5, title: 'Billing Setup' },
];

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#1f2937',
  border: '1px solid #4b5563',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: 14,
  color: '#e5e7eb',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#d1d5db',
  marginBottom: 6,
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 4,
};

const btnPrimary: React.CSSProperties = {
  backgroundColor: '#7c3aed',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 24px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  backgroundColor: 'transparent',
  color: '#9ca3af',
  border: '1px solid #4b5563',
  borderRadius: 8,
  padding: '10px 16px',
  fontSize: 14,
  cursor: 'pointer',
};

// ── Step 1: Organisation Profile ──────────────────────────────────────────────

function Step1OrgProfile({ onSave, isSaving, initialData }: StepProps) {
  const [form, setForm] = useState({
    companyName: initialData?.companyName || '',
    primaryContact: initialData?.primaryContact || '',
    timezone: initialData?.timezone || 'Asia/Kolkata',
    billingEmail: initialData?.billingEmail || '',
  });

  const valid = form.companyName.trim() && form.primaryContact.trim() && form.billingEmail.trim();

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#f3f4f6' }}>
        Organisation Profile
      </h1>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
        This data populates the white-label configuration your clients will see.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Company name *</label>
          <input
            style={inputStyle}
            value={form.companyName}
            onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
            placeholder="Indium Software"
          />
        </div>
        <div>
          <label style={labelStyle}>Primary contact name *</label>
          <input
            style={inputStyle}
            value={form.primaryContact}
            onChange={(e) => setForm((f) => ({ ...f, primaryContact: e.target.value }))}
            placeholder="Ramesh Kumar"
          />
        </div>
        <div>
          <label style={labelStyle}>Timezone</label>
          <select
            style={inputStyle}
            value={form.timezone}
            onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
          >
            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
            <option value="America/New_York">America/New_York (EST)</option>
            <option value="America/Chicago">America/Chicago (CST)</option>
            <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="Europe/Berlin">Europe/Berlin (CET)</option>
            <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
            <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Billing email *</label>
          <input
            type="email"
            style={inputStyle}
            value={form.billingEmail}
            onChange={(e) => setForm((f) => ({ ...f, billingEmail: e.target.value }))}
            placeholder="billing@indiumsoftware.com"
          />
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <button style={{ ...btnPrimary, opacity: valid ? 1 : 0.5 }} disabled={isSaving || !valid} onClick={() => onSave(form)}>
          {isSaving ? 'Saving...' : 'Continue \u2192'}
        </button>
      </div>
    </div>
  );
}

// ── Step 2: White-Label Branding ──────────────────────────────────────────────

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg'];
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

function Step2Branding({ onSave, onBack, isSaving, initialData }: StepProps) {
  const [form, setForm] = useState({
    platformName: initialData?.platformName || '',
    accentColor: initialData?.accentColor || '#6C4DFF',
    logoUrl: initialData?.logoUrl || '',
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const valid = form.platformName.trim();

  const resolveLogoSrc = (url: string): string => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
    return `${API_BASE}${url}`;
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError(null);

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError('Unsupported file type. Use PNG or JPEG.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setLogoError('File too large. Maximum 2MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setLogoUploading(true);
    setLogoFileName(file.name);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/partner/logo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      }

      const { logoUrl } = (await res.json()) as { logoUrl: string };
      setForm((f) => ({ ...f, logoUrl }));
    } catch (err: any) {
      setLogoError(err?.message || 'Logo upload failed');
      setLogoFileName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoError(null);
    setLogoUploading(true);
    try {
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/partner/logo`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Remove failed (HTTP ${res.status})`);
      }
      setForm((f) => ({ ...f, logoUrl: '' }));
      setLogoFileName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setLogoError(err?.message || 'Logo remove failed');
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#f3f4f6' }}>
        White-Label Branding
      </h1>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
        Configure how your clients see the platform. BluePrint is invisible unless you choose to show it.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Partner logo (optional)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={handleLogoSelect}
            style={{ display: 'none' }}
            data-testid="logo-file-input"
          />
          {form.logoUrl ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: 12,
                backgroundColor: '#1f2937',
                border: '1px solid #4b5563',
                borderRadius: 6,
              }}
            >
              <img
                src={resolveLogoSrc(form.logoUrl)}
                alt="Partner logo preview"
                style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain' }}
              />
              <div style={{ flex: 1, fontSize: 12, color: '#9ca3af' }}>
                {logoFileName || 'Logo uploaded'}
              </div>
              <button type="button" style={btnSecondary} onClick={handleRemoveLogo}>
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              style={{ ...btnSecondary, opacity: logoUploading ? 0.6 : 1 }}
              disabled={logoUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {logoUploading ? 'Uploading…' : 'Upload logo'}
            </button>
          )}
          {logoError && (
            <p style={{ ...hintStyle, color: '#f87171', marginTop: 8 }}>{logoError}</p>
          )}
          <p style={hintStyle}>
            Shown in client emails and the client portal header. Recommended: PNG or JPEG, minimum 200px
            wide, max 2&nbsp;MB.
          </p>
        </div>
        <div>
          <label style={labelStyle}>Platform name *</label>
          <input
            style={inputStyle}
            value={form.platformName}
            onChange={(e) => setForm((f) => ({ ...f, platformName: e.target.value }))}
            placeholder="Indium Delivery Platform"
          />
          <p style={hintStyle}>What your clients see &mdash; e.g. &ldquo;Indium Delivery Platform&rdquo;</p>
        </div>
        <div>
          <label style={labelStyle}>Accent colour</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="color"
              value={form.accentColor}
              onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))}
              style={{ width: 40, height: 40, borderRadius: 6, cursor: 'pointer', border: 'none' }}
            />
            <input
              style={{ ...inputStyle, width: 128, fontFamily: 'monospace' }}
              value={form.accentColor}
              onChange={(e) => setForm((f) => ({ ...f, accentColor: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div style={{ marginTop: 24, border: '1px solid #374151', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '8px 16px', backgroundColor: '#1f2937', fontSize: 12, color: '#6b7280' }}>
          Preview — what clients see
        </div>
        <div style={{ padding: 16, borderTop: `3px solid ${form.accentColor}` }}>
          {form.logoUrl ? (
            <img
              src={resolveLogoSrc(form.logoUrl)}
              alt={form.platformName || 'Partner logo'}
              style={{ maxHeight: 40, maxWidth: 200, display: 'block', marginBottom: 6 }}
            />
          ) : (
            <p style={{ fontWeight: 700, color: form.accentColor, margin: 0 }}>
              {form.platformName || 'Your Platform Name'}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginBottom: 0 }}>Powered by BluePrint</p>
        </div>
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button style={btnSecondary} onClick={onBack} disabled={logoUploading}>&larr; Back</button>
        <button
          style={{ ...btnPrimary, opacity: valid && !logoUploading ? 1 : 0.5 }}
          disabled={isSaving || !valid || logoUploading}
          onClick={() => {
            // logoUrl is server-owned (POST /partner/logo writes it to
            // partner_accounts). Do NOT round-trip it through this PATCH —
            // the backend rejects unknown keys via a strict Zod schema.
            onSave({
              platformName: form.platformName,
              accentColor: form.accentColor,
            });
          }}
          title={logoUploading ? 'Please wait for the logo upload to finish.' : undefined}
        >
          {isSaving ? 'Saving...' : logoUploading ? 'Uploading logo...' : 'Continue \u2192'}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Default Configuration ─────────────────────────────────────────────

const ARTEFACT_TYPE_OPTIONS = [
  { value: 'prd', label: 'PRD' },
  { value: 'sprint_backlog', label: 'Sprint Backlog' },
  { value: 'qa_report', label: 'QA Summary' },
  { value: 'deployment_runbook', label: 'Deployment' },
];

const DEFAULT_ROLE_ARTEFACTS: Record<string, string[]> = {
  CLIENT_SPONSOR: ['prd', 'sprint_backlog', 'qa_report', 'deployment_runbook'],
  BUSINESS_ANALYST: ['prd', 'sprint_backlog'],
  SIGN_OFF_AUTHORITY: ['deployment_runbook'],
  END_USER_REP: ['prd'],
};

const BUSINESS_ROLES = [
  { key: 'CLIENT_SPONSOR', label: 'Client Sponsor' },
  { key: 'BUSINESS_ANALYST', label: 'Business Analyst' },
  { key: 'SIGN_OFF_AUTHORITY', label: 'Sign-Off Authority' },
  { key: 'END_USER_REP', label: 'End User Rep' },
];


export function Step3DefaultConfig({ onSave, onBack, isSaving, initialData }: StepProps) {
  const govData = initialData?.clientGovernance;

  const [form, setForm] = useState({
    defaultInfrastructure: initialData?.defaultInfrastructure || 'MANAGED',
    defaultEstimationType: initialData?.defaultEstimationType || 'EU_RU',
    defaultRevocationWindowH: initialData?.defaultRevocationWindowH || 72,
  });

  // Step 3a: Client governance defaults
  // #3185 APPROVE.2 2c (ruling D8): the client approval matrix replaces the
  // three require-client toggles; the old keys aren't sent or translated.
  const [approvalMatrix, setApprovalMatrix] = useState<PartnerMatrix>(
    fullPartnerMatrix(govData?.approvalConfig?.clientApprovalMatrix),
  );
  const [revocationWindow, setRevocationWindow] = useState<number>(
    govData?.approvalConfig?.revocationWindowHours ?? 24,
  );
  const [ackWindow, setAckWindow] = useState<number>(
    govData?.approvalConfig?.acknowledgementWindowHours ?? 24,
  );
  const [approvalMode, setApprovalMode] = useState<string>(
    govData?.approvalConfig?.defaultApprovalMode ?? 'ROLE_BASED',
  );
  const [roleOverrides, setRoleOverrides] = useState<Record<string, string[]>>(
    govData?.roleTierOverrides ?? { ...DEFAULT_ROLE_ARTEFACTS },
  );

  const toggleRoleArtefact = (role: string, artefact: string) => {
    setRoleOverrides((prev) => {
      const current = prev[role] ?? [];
      const next = current.includes(artefact)
        ? current.filter((a) => a !== artefact)
        : [...current, artefact];
      return { ...prev, [role]: next };
    });
  };

  const handleSave = () => {
    onSave({
      ...form,
      clientGovernance: {
        approvalConfig: {
          clientApprovalMatrix: compactPartnerMatrix(approvalMatrix),
          revocationWindowHours: revocationWindow,
          acknowledgementWindowHours: ackWindow,
          defaultApprovalMode: approvalMode,
        },
        roleTierOverrides: roleOverrides,
      },
    });
  };

  // MODE.C5/b (#1185) — value flipped from 'BYOI' to 'BYOC' so the stored
  // value matches the user-visible label ("Bring Your Own Cloud"). This
  // was the canonical example of the half-converted state the audit
  // flagged (§C.3).
  const infraOptions = [
    { value: 'MANAGED', label: 'BluePrint Managed', desc: 'Recommended. No cloud account needed.' },
    { value: 'BYOC', label: 'Bring Your Own Cloud', desc: 'Client uses their own AWS/Azure/GCP.' },
  ];

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: 16, fontWeight: 700, color: '#e5e7eb', marginBottom: 12, marginTop: 0,
    paddingBottom: 8, borderBottom: '1px solid #374151',
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#f3f4f6' }}>
        Default Configuration
      </h1>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
        These defaults apply to all new client tenants you create. They can be overridden per client.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <label style={labelStyle}>Default infrastructure</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {infraOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, defaultInfrastructure: opt.value }))}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: `1px solid ${form.defaultInfrastructure === opt.value ? '#7c3aed' : '#4b5563'}`,
                  backgroundColor: form.defaultInfrastructure === opt.value ? 'rgba(124,58,237,0.15)' : 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#e5e7eb',
                }}
              >
                <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{opt.label}</p>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, marginBottom: 0 }}>{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Default estimation type</label>
          <select
            style={inputStyle}
            value={form.defaultEstimationType}
            onChange={(e) => setForm((f) => ({ ...f, defaultEstimationType: e.target.value }))}
          >
            <option value="EU_RU">EU/RU (Effort Units + Review Units)</option>
            <option value="STORY_POINTS">Story Points</option>
            <option value="BOTH">Both</option>
          </select>
        </div>
      </div>

      {/* ── Step 3a: Client Governance Defaults ───────────────────────────── */}

      <div style={{ marginTop: 32, border: '1px solid #374151', borderRadius: 8, padding: 20 }}>
        <h2 style={sectionHeaderStyle}>Client Governance Defaults</h2>

        {/* Approval Requirements */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Client approvals</label>
          <p style={hintStyle}>
            Copied into each new client&rsquo;s workspace when you link the client. {D9_COPY}
          </p>
          <div style={{ marginTop: 8 }}>
            <PartnerClientApprovalMatrix value={approvalMatrix} onChange={setApprovalMatrix} />
          </div>
        </div>

        {/* Default Windows */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Default Windows</label>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 6px' }}>Revocation window</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {[24, 48, 72].map((h) => (
                  <button key={h} type="button" onClick={() => setRevocationWindow(h)} style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 13,
                    border: `1px solid ${revocationWindow === h ? '#7c3aed' : '#4b5563'}`,
                    backgroundColor: revocationWindow === h ? 'rgba(124,58,237,0.15)' : 'transparent',
                    color: revocationWindow === h ? '#c4b5fd' : '#9ca3af', cursor: 'pointer',
                  }}>{h}h</button>
                ))}
              </div>
            </div>
            <div>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 6px' }}>Acknowledgement window</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {[24, 48, 72].map((h) => (
                  <button key={h} type="button" onClick={() => setAckWindow(h)} style={{
                    padding: '6px 12px', borderRadius: 6, fontSize: 13,
                    border: `1px solid ${ackWindow === h ? '#7c3aed' : '#4b5563'}`,
                    backgroundColor: ackWindow === h ? 'rgba(124,58,237,0.15)' : 'transparent',
                    color: ackWindow === h ? '#c4b5fd' : '#9ca3af', cursor: 'pointer',
                  }}>{h}h</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Default Approval Mode */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Default Approval Mode</label>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { value: 'SINGLE_APPROVER', label: 'Single Approver', desc: 'Any PRODUCT_MANAGER can approve.' },
              { value: 'ROLE_BASED', label: 'Role-Based', desc: 'Enforces full role matrix.' },
            ].map((opt) => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, padding: 12, borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${approvalMode === opt.value ? '#7c3aed' : '#4b5563'}`,
                backgroundColor: approvalMode === opt.value ? 'rgba(124,58,237,0.15)' : 'transparent',
                flex: 1,
              }}>
                <input type="radio" name="approvalMode" value={opt.value} checked={approvalMode === opt.value}
                  onChange={() => setApprovalMode(opt.value)} style={{ accentColor: '#7c3aed', marginTop: 2 }} />
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: '#e5e7eb' }}>{opt.label}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Business Tier Role Defaults */}
        <div>
          <label style={labelStyle}>Business Tier Role Defaults</label>
          <p style={hintStyle}>Configure which artefact types each business role can approve by default.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 13, color: '#9ca3af', borderBottom: '1px solid #374151' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: 13, color: '#9ca3af', borderBottom: '1px solid #374151' }}>Can Approve</th>
              </tr>
            </thead>
            <tbody>
              {BUSINESS_ROLES.map((role) => (
                <tr key={role.key}>
                  <td style={{ padding: '8px 0', fontSize: 14, color: '#d1d5db', borderBottom: '1px solid #1f2937', whiteSpace: 'nowrap' }}>
                    {role.label}
                  </td>
                  <td style={{ padding: '8px 0', borderBottom: '1px solid #1f2937' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {ARTEFACT_TYPE_OPTIONS.map((art) => {
                        const selected = (roleOverrides[role.key] ?? []).includes(art.value);
                        return (
                          <button key={art.value} type="button"
                            onClick={() => toggleRoleArtefact(role.key, art.value)}
                            style={{
                              padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                              border: `1px solid ${selected ? '#7c3aed' : '#4b5563'}`,
                              backgroundColor: selected ? 'rgba(124,58,237,0.2)' : 'transparent',
                              color: selected ? '#c4b5fd' : '#6b7280',
                            }}>
                            {art.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button style={btnSecondary} onClick={onBack}>&larr; Back</button>
        <button style={btnPrimary} disabled={isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Continue \u2192'}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Domain Pack Access ────────────────────────────────────────────────

function Step4DomainPacks({ onSave, onBack, isSaving, initialData }: StepProps) {
  const [selected, setSelected] = useState<string[]>(initialData?.selectedPacks || []);

  const PACKS = [
    { id: 'healthcare', name: 'Healthcare Pack', description: 'HIPAA, HL7 FHIR, NABH, DISHA', available: true },
    { id: 'bfsi', name: 'BFSI Pack', description: 'RBI, PCI-DSS, SWIFT', comingSoon: true },
    { id: 'edtech', name: 'EdTech Pack', description: 'FERPA, CIPA, accessibility', comingSoon: true },
  ];

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#f3f4f6' }}>
        Domain Pack Access
      </h1>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
        Domain packs add industry-specific compliance checks and gap questions to the PM Agent.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {PACKS.map((pack) => (
          <div
            key={pack.id}
            onClick={() => !pack.comingSoon && toggle(pack.id)}
            style={{
              border: `1px solid ${selected.includes(pack.id) ? '#7c3aed' : '#374151'}`,
              borderRadius: 8,
              padding: 16,
              cursor: pack.comingSoon ? 'default' : 'pointer',
              opacity: pack.comingSoon ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <p style={{ fontWeight: 600, fontSize: 14, margin: 0, color: '#e5e7eb' }}>{pack.name}</p>
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, marginBottom: 0 }}>{pack.description}</p>
            </div>
            {pack.comingSoon ? (
              <span
                style={{
                  fontSize: 12,
                  color: '#6b7280',
                  border: '1px solid #374151',
                  borderRadius: 4,
                  padding: '2px 8px',
                }}
              >
                Coming S11
              </span>
            ) : (
              <input
                type="checkbox"
                checked={selected.includes(pack.id)}
                readOnly
                style={{ width: 16, height: 16, accentColor: '#7c3aed' }}
              />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={btnSecondary} onClick={onBack}>&larr; Back</button>
        <button style={btnPrimary} disabled={isSaving} onClick={() => onSave({ selectedPacks: selected })}>
          {isSaving ? 'Saving...' : 'Continue \u2192'}
        </button>
      </div>
    </div>
  );
}

// ── Step 5: Billing Setup ─────────────────────────────────────────────────────

export function Step5Billing({ onSave, onBack, isSaving }: StepProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);
  const [helperVisible, setHelperVisible] = useState(false);

  const handleTermsClick = () => {
    setTermsViewed(true);
    setHelperVisible(false);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!termsViewed) {
      setHelperVisible(true);
      return;
    }
    setAcknowledged(e.target.checked);
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#f3f4f6' }}>
        Billing Setup
      </h1>
      <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 24 }}>
        BluePrint bills at the partner level — consolidated across all your client tenants.
        You invoice clients separately under your own terms.
      </p>
      <div style={{ border: '1px solid #374151', borderRadius: 8, padding: 16, marginBottom: 24 }}>
        <h3 style={{ fontWeight: 600, fontSize: 14, marginTop: 0, marginBottom: 12, color: '#e5e7eb' }}>
          PLATFORM_PARTNER Plan
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          {[
            ['Platform access', 'Unlimited projects + clients'],
            ['Billing model', '$199/month base + $39/client/month'],
            ['Pack builder', 'Included'],
            ['White-label', 'Included'],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#9ca3af' }}>{label}</span>
              <span style={{ color: '#e5e7eb' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          backgroundColor: 'rgba(59,130,246,0.1)',
          border: '1px solid #3b82f6',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <p style={{ fontSize: 14, color: '#93c5fd', margin: 0 }}>
          BluePrint bills at the partner level — $199/month base plus $39 per
          active client per month. You invoice your clients separately under
          your own terms. The BluePrint team will be in touch within 1 business
          day to confirm your account activation.
        </p>
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          cursor: termsViewed ? 'pointer' : 'not-allowed',
          marginBottom: 8,
          opacity: termsViewed ? 1 : 0.75,
        }}
      >
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={handleCheckboxChange}
          disabled={!termsViewed}
          aria-describedby="terms-helper"
          style={{ marginTop: 2, accentColor: '#7c3aed' }}
        />
        <span style={{ fontSize: 14, color: '#d1d5db' }}>
          I have read and agree to the{' '}
          <a
            href="/terms/partner"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleTermsClick}
            style={{ color: '#a78bfa', textDecoration: 'underline', fontWeight: 600 }}
          >
            PLATFORM_PARTNER Programme Terms
          </a>
          {' '}and understand that these terms govern my use of the platform.
        </span>
      </label>
      {helperVisible && !termsViewed && (
        <p
          id="terms-helper"
          role="alert"
          style={{ fontSize: 12, color: '#fbbf24', margin: '0 0 24px 36px' }}
        >
          Please read the terms before agreeing.
        </p>
      )}
      {(!helperVisible || termsViewed) && <div style={{ marginBottom: 24 }} />}
      <div style={{ display: 'flex', gap: 12 }}>
        <button style={btnSecondary} onClick={onBack}>&larr; Back</button>
        <button
          style={{ ...btnPrimary, opacity: acknowledged ? 1 : 0.5 }}
          disabled={isSaving || !acknowledged}
          onClick={() =>
            onSave({
              acknowledged,
              acceptedTermsVersion: PARTNER_TERMS_VERSION,
              completedAt: new Date().toISOString(),
            })
          }
        >
          {isSaving ? 'Activating...' : 'Activate Partner Account \u2192'}
        </button>
      </div>
    </div>
  );
}

// ── Main Wizard Page ──────────────────────────────────────────────────────────

export default function PartnerOnboardingWizardPage() {
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiFetch<WizardState>('/partner/wizard')
      .then((w) => {
        if (w.completedAt) {
          navigate('/partner');
          return;
        }
        setWizard(w);
        setCurrentStep(w.currentStep);
      })
      .catch((err) => setError(err.message));
  }, [navigate]);

  const saveStep = async (stepData: any) => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await apiFetch<{ saved: boolean; nextStep: number; completedSteps: number[] }>(
        `/partner/wizard/step/${currentStep}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(stepData) },
      );
      setCurrentStep(result.nextStep);
      setWizard((prev) =>
        prev
          ? { ...prev, completedSteps: result.completedSteps, currentStep: result.nextStep, [`step${currentStep}Data`]: stepData }
          : prev,
      );

      // If step 5 was just completed, finalise
      if (currentStep === 5) {
        await apiFetch('/partner/wizard/complete', { method: 'POST' });
        navigate('/partner');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  if (!wizard && !error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#030712', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6b7280' }}>Loading wizard...</p>
      </div>
    );
  }

  const stepProps: StepProps = {
    onSave: saveStep,
    onBack: goBack,
    isSaving,
    initialData: wizard ? (wizard as any)[`step${currentStep}Data`] : null,
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#030712', display: 'flex' }}>
      {/* Left sidebar — step progress */}
      <div style={{ width: 256, borderRight: '1px solid #1f2937', padding: 24, flexShrink: 0 }}>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#a78bfa', margin: 0 }}>Partner Setup</h2>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
            Complete all steps to activate
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {STEPS.map((step) => {
            const isCompleted = wizard?.completedSteps.includes(step.number);
            const isCurrent = currentStep === step.number;
            return (
              <button
                key={step.number}
                type="button"
                onClick={() => {
                  // Allow navigating to completed steps or current step
                  if (isCompleted || isCurrent) setCurrentStep(step.number);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  borderRadius: 8,
                  border: isCurrent ? '1px solid #6d28d9' : '1px solid transparent',
                  backgroundColor: isCurrent ? 'rgba(109,40,217,0.15)' : 'transparent',
                  cursor: isCompleted || isCurrent ? 'pointer' : 'default',
                  color: isCompleted ? '#9ca3af' : isCurrent ? '#e5e7eb' : '#4b5563',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                    backgroundColor: isCompleted ? '#16a34a' : isCurrent ? '#7c3aed' : '#1f2937',
                    color: isCompleted || isCurrent ? '#fff' : '#6b7280',
                  }}
                >
                  {isCompleted ? '\u2713' : step.number}
                </div>
                <span style={{ fontSize: 14 }}>{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, padding: 32, maxWidth: 640 }}>
        {error && (
          <div
            style={{
              backgroundColor: 'rgba(239,68,68,0.1)',
              border: '1px solid #ef4444',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              color: '#ef4444',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
        {currentStep === 1 && <Step1OrgProfile {...stepProps} />}
        {currentStep === 2 && <Step2Branding {...stepProps} />}
        {currentStep === 3 && <Step3DefaultConfig {...stepProps} />}
        {currentStep === 4 && <Step4DomainPacks {...stepProps} />}
        {currentStep === 5 && <Step5Billing {...stepProps} />}
      </div>
    </div>
  );
}
